/**
 * 日付操作のユーティリティ関数
 */

/**
 * 日付をフォーマットして文字列で返します
 * @param date - 対象の日付
 * @param format - フォーマット文字列（例: 'yyyy-MM-dd'）
 * @returns フォーマットされた日付文字列
 */
export const formatDate = (date: Date, format: string): string => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  // フォーマット文字列内の各パターンを実際の値に置換
  return format
    .replace(/yyyy/g, year.toString())
    .replace(/MM/g, month < 10 ? `0${month}` : month.toString())
    .replace(/dd/g, day < 10 ? `0${day}` : day.toString())
    .replace(/HH/g, hours < 10 ? `0${hours}` : hours.toString())
    .replace(/mm/g, minutes < 10 ? `0${minutes}` : minutes.toString())
    .replace(/ss/g, seconds < 10 ? `0${seconds}` : seconds.toString());
};

/**
 * 2つの日付が同じ日かどうかを確認
 * @param date1 - 比較する日付1
 * @param date2 - 比較する日付2
 * @returns 同じ日の場合はtrue
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * 時間文字列（例: "09:30"）を分数に変換
 * @param timeStr - 時間文字列 ("HH:MM" 形式)
 * @returns 分数（時間 * 60 + 分）
 */
export const timeStringToMinutes = (timeStr: string): number => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * 分数を時間文字列に変換
 * @param minutes - 分数
 * @returns 時間文字列 ("HH:MM" 形式)
 */
export const minutesToTimeString = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours < 10 ? `0${hours}` : hours}:${mins < 10 ? `0${mins}` : mins}`;
};

/**
 * 開始時間文字列を正規化（nullや空文字列の場合は00:00に）
 * @param timeStr - 時間文字列
 * @returns 正規化された時間文字列
 */
export const resolveStartTime = (timeStr: string | null | undefined): string => {
  if (!timeStr) return "00:00";
  return timeStr;
};

/**
 * 終了時間文字列を正規化（nullや空文字列の場合は00:00に）
 * @param timeStr - 時間文字列
 * @returns 正規化された時間文字列
 */
export const resolveEndTime = (timeStr: string | null | undefined): string => {
  if (!timeStr) return "00:00";
  return timeStr;
};

/**
 * ユニーク文字列の生成（ID用）
 * @returns ユニークID文字列
 */
export const generateUniqueId = (): string => {
  return Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
};

/**
 * 日付をYYYY-MM-DD形式の文字列に変換
 * @param date - 変換する日付
 * @returns YYYY-MM-DD形式の文字列
 */
export const toISODateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 時間差を計算（分単位）
 * @param startTime 開始時間（HH:MM形式またはISO形式）
 * @param endTime 終了時間（HH:MM形式またはISO形式）
 * @returns 時間差（分）
 */
export const calcDuration = (startTime: string, endTime: string): number => {
  // デバッグ出力
  if (process.env.NODE_ENV === 'development') {
    console.log(`時間差計算: ${startTime} to ${endTime}`);
  }
  
  let startDate: Date;
  let endDate: Date;
  
  // ISO形式かHH:MM形式かを判断して処理
  if (startTime.includes('T')) {
    // ISO形式の場合
    startDate = new Date(startTime);
    endDate = new Date(endTime);
  } else {
    // HH:MM形式の場合は今日の日付と組み合わせる
    const today = new Date();
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    startDate = new Date(today);
    startDate.setHours(startHour, startMinute, 0, 0);
    
    endDate = new Date(today);
    endDate.setHours(endHour, endMinute, 0, 0);
    
    // endDateが翌日になる場合の処理
    if (endDate < startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }
  }
  
  const durationMs = endDate.getTime() - startDate.getTime();
  const durationMinutes = Math.round(durationMs / (1000 * 60));
  
  // デバッグ出力
  if (process.env.NODE_ENV === 'development') {
    console.log(`計算結果: ${durationMinutes}分 (${startDate.toLocaleTimeString()} to ${endDate.toLocaleTimeString()})`);
  }
  
  return durationMinutes;
};

/**
 * 指定した日数を日付に追加する
 * @param date - 元の日付
 * @param days - 追加する日数
 * @returns 新しい日付
 */
export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * 日付をパースする (ISO形式)
 * @param dateStr - ISO形式の日付文字列
 * @returns 日付オブジェクト
 */
export const parseISODate = (dateStr: string): Date => {
  return new Date(dateStr);
};

/**
 * 日本の曜日（日、月、火、水、木、金、土）を取得
 * @param date - 日付
 * @returns 曜日の文字列
 */
export const getJapaneseDayOfWeek = (date: Date): string => {
  const days = ['日', '月', '火', '水', '木', '金', '土'];
  return days[date.getDay()];
};

/**
 * 日時文字列に対するタイムゾーン調整を適用
 * @param dateTimeString - 日時文字列
 * @returns タイムゾーン調整済みの日時文字列
 */
export const applyTimeZoneOffset = (dateTimeString: string): string => {
  if (!dateTimeString) return '';
  const date = new Date(dateTimeString);
  const offset = date.getTimezoneOffset() * 60000; // オフセットをミリ秒に変換
  const localDate = new Date(date.getTime() - offset);
  return localDate.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM形式に整形
};

/**
 * 日時文字列から日付部分のみを抽出
 * @param dateTimeString - 日時文字列
 * @returns YYYY-MM-DD形式の日付文字列
 */
export const extractDatePart = (dateTimeString: string): string => {
  if (!dateTimeString) return '';
  return dateTimeString.split('T')[0];
};

/**
 * 日時文字列から時間部分のみを抽出
 * @param dateTimeString - 日時文字列
 * @returns HH:MM形式の時間文字列
 */
export const extractTimePart = (dateTimeString: string): string => {
  if (!dateTimeString) return '';
  const parts = dateTimeString.split('T');
  return parts.length > 1 ? parts[1].substring(0, 5) : '';
}; 